import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fieldOpsQueueOwnersApi } from '../api/field-ops-queue-owners-api';
import { fieldOpsPeopleApi } from '../api/field-ops-people-api';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect } from '../components/SearchableSelect';
import {
  fieldOpsTeams,
  queueOwnerScopeLabels,
  queueOwnerScopes,
  teamLabels,
  type FieldOpsQueueOwnerInput,
  type FieldOpsTeam,
  type QueueOwnerScope,
} from '../domain/fieldOpsTeam';
import {
  fieldWorkSources,
  sourceLabels,
  type FieldWorkSource,
} from '../domain/fieldWorkRequest';

const emptyOwner: FieldOpsQueueOwnerInput = {
  displayName: '',
  email: '',
  scope: 'EUROPE',
  priority: 10,
  available: true,
  active: true,
};

export function FieldWorkSettingsPage() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<FieldOpsQueueOwnerInput>(emptyOwner);
  const [showForm, setShowForm] = useState(false);

  const owners = useQuery({
    queryKey: ['field-ops-queue-owners'],
    queryFn: () => fieldOpsQueueOwnersApi.listQueueOwners(true),
  });
  const people = useQuery({
    queryKey: ['field-ops-people'],
    queryFn: () => fieldOpsPeopleApi.listFieldOpsPeople(),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['field-ops-queue-owners'] });

  const tagPerson = useMutation({
    mutationFn: fieldOpsPeopleApi.updateFieldOpsPersonTags.bind(fieldOpsPeopleApi),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['field-ops-people'] }),
  });

  const update = useMutation({
    mutationFn: fieldOpsQueueOwnersApi.updateQueueOwner.bind(fieldOpsQueueOwnersApi),
    onSuccess: refresh,
  });
  const create = useMutation({
    mutationFn: fieldOpsQueueOwnersApi.createQueueOwner.bind(fieldOpsQueueOwnersApi),
    onSuccess: () => {
      setDraft(emptyOwner);
      setShowForm(false);
      void refresh();
    },
  });

  if (owners.isLoading) return <PageLoadingState label="Loading queue owners…" />;
  if (owners.isError) {
    return (
      <ErrorState
        title="Queue owners could not be loaded"
        rpcName="ListFieldOpsQueueOwners"
        onRetry={() => void owners.refetch()}
      />
    );
  }

  return (
    <>
      <PageHeader
        backLink={<Link to="/field-work">Field work</Link>}
        title="Queue owners"
        description="Who owns each Field Ops team's queue. An unavailable regional owner falls back to the all-teams backup."
      />

      <section className="drawer-section" aria-label="Queue owners">
        <div className="drawer-actions">
          <button type="button" onClick={() => setShowForm((value) => !value)}>
            {showForm ? 'Cancel' : 'Add owner'}
          </button>
        </div>

        {showForm ? (
          <form
            className="form-row"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate(draft);
            }}
          >
            <label>
              Name
              <input
                required
                value={draft.displayName}
                onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
              />
            </label>
            <label>
              Email
              <input
                required
                type="email"
                value={draft.email}
                onChange={(event) => setDraft({ ...draft, email: event.target.value })}
              />
            </label>
            <label>
              Scope
              <SearchableSelect
                ariaLabel="Scope"
                value={draft.scope}
                onChange={(value) => setDraft({ ...draft, scope: value as QueueOwnerScope })}
                options={queueOwnerScopes.map((scope) => ({
                  value: scope,
                  label: queueOwnerScopeLabels[scope],
                }))}
                placeholder="Scope"
              />
            </label>
            <label>
              Priority
              <input
                type="number"
                min={0}
                value={draft.priority}
                onChange={(event) =>
                  setDraft({ ...draft, priority: Number(event.target.value) || 0 })
                }
              />
            </label>
            <button type="submit" className="primary" disabled={create.isPending}>
              Save owner
            </button>
          </form>
        ) : null}

        {create.isError ? (
          <p role="alert" className="form-error">
            {create.error instanceof Error ? create.error.message : 'Could not add the owner.'}
          </p>
        ) : null}
        {update.isError ? (
          <p role="alert" className="form-error">
            {update.error instanceof Error ? update.error.message : 'Could not save the change.'}
          </p>
        ) : null}

        <div className="table-wrap reference-table">
          <table>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Scope</th>
                <th scope="col" className="num">
                  Priority
                </th>
                <th scope="col" className="col-center">
                  Available
                </th>
                <th scope="col" className="col-center">
                  Active
                </th>
              </tr>
            </thead>
            <tbody>
              {(owners.data ?? []).map((owner) => (
                <tr key={owner.id}>
                  <th scope="row">
                    {owner.displayName}
                    <br />
                    <a href={`mailto:${owner.email}`} className="mono">
                      {owner.email}
                    </a>
                  </th>
                  <td>{queueOwnerScopeLabels[owner.scope]}</td>
                  <td className="mono num">{owner.priority}</td>
                  <td className="col-center">
                    <input
                      type="checkbox"
                      checked={owner.available}
                      disabled={update.isPending}
                      aria-label={`${owner.displayName} available`}
                      onChange={(event) =>
                        update.mutate({
                          id: owner.id,
                          version: owner.version,
                          available: event.target.checked,
                        })
                      }
                    />
                  </td>
                  <td className="col-center">
                    <input
                      type="checkbox"
                      checked={owner.active}
                      disabled={update.isPending}
                      aria-label={`${owner.displayName} active`}
                      onChange={(event) =>
                        update.mutate({
                          id: owner.id,
                          version: owner.version,
                          active: event.target.checked,
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="drawer-version-note">
          Routing order: the team's own owner (lowest priority number wins), then the all-teams
          backup.
        </p>
      </section>

      <section className="drawer-section" aria-label="Field Ops directory">
        <h2>Field Ops directory</h2>
        <p className="drawer-version-note">
          Source and Team here prefill a new request for whoever raises it. Seeded from the
          employee sheet; edits are re-applied on top of the seed and survive a redeploy.
        </p>
        {tagPerson.isError ? (
          <p role="alert" className="form-error">
            {tagPerson.error instanceof Error
              ? tagPerson.error.message
              : 'Could not save the tag.'}
          </p>
        ) : null}
        {people.isLoading ? (
          <p>Loading directory…</p>
        ) : people.isError ? (
          <p role="alert" className="form-error">
            The directory could not be loaded.
          </p>
        ) : (
          <div className="table-wrap reference-table">
            <table>
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Source</th>
                  <th scope="col">Team</th>
                </tr>
              </thead>
              <tbody>
                {[...(people.data ?? [])]
                  .sort((a, b) => a.displayName.localeCompare(b.displayName))
                  .map((person) => (
                    <tr key={person.email}>
                      <th scope="row">
                        {person.displayName}
                        <br />
                        <span className="mono">{person.email}</span>
                      </th>
                      <td>
                        <SearchableSelect
                          ariaLabel={`${person.displayName} source`}
                          value={person.source ?? ''}
                          onChange={(value) =>
                            tagPerson.mutate({
                              email: person.email,
                              version: person.version ?? 0,
                              source: (value || undefined) as FieldWorkSource | undefined,
                              team: person.team,
                            })
                          }
                          options={[
                            { value: '', label: 'Unset' },
                            ...fieldWorkSources.map((source) => ({
                              value: source,
                              label: sourceLabels[source],
                            })),
                          ]}
                          placeholder="Unset"
                        />
                      </td>
                      <td>
                        <SearchableSelect
                          ariaLabel={`${person.displayName} team`}
                          value={person.team ?? ''}
                          onChange={(value) =>
                            tagPerson.mutate({
                              email: person.email,
                              version: person.version ?? 0,
                              source: person.source,
                              team: (value || undefined) as FieldOpsTeam | undefined,
                            })
                          }
                          options={[
                            { value: '', label: 'Unset' },
                            ...fieldOpsTeams.map((team) => ({
                              value: team,
                              label: teamLabels[team],
                            })),
                          ]}
                          placeholder="Unset"
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
